import { toStringBigDecimal } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import { createEvmActionSource, isHex } from '@pkg/evm'
import { createSolanaActionSource } from '@pkg/solana'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import {
  type BinanceExchange,
  binanceExchange,
  networkEvmChainIds,
  solanaNetwork,
} from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

/**
 * 取引所ごとの 出金履歴 データ取得関数を作成する
 * @param params.exchange Binance API を利用する取引所の識別情報
 * @returns 出金履歴 データ取得関数
 */
export const createFetchApiData =
  ({
    exchange,
  }: {
    exchange: BinanceExchange
  }): FetchApiData =>
  async ({
    apiKey,
    apiSecret,
    assets,
    config,
    from,
    createHeaders,
    proxy,
    symbolAssetMap,
    to,
  }) => {
    if (!apiSecret) {
      throw new ApiSecretRequiredException()
    }

    const getAsset = createGetAsset({ assets, symbolAssetMap })

    const client = createClient({
      accountCategory: exchange.accountCategory,
      apiKey,
      apiSecret,
      createHeaders,
      proxy,
    })

    const response = await client.run({
      method: 'GET',
      path: '/sapi/v1/capital/withdraw/history',
      query: {
        limit: config.limit.toString(),
        ...(from ? { startTime: from.toString() } : {}),
        ...(to ? { endTime: to.toString() } : {}),
      },
      responseSchema,
    })

    // 昇順 sort by applyTime
    const sortedWithdrawals = response.sort((a, b) => a.applyTime - b.applyTime)

    // データ処理
    let stopped = false
    let fetchedFrom: number | undefined
    let fetchedTo: number | undefined
    const sources: string[] = []
    const actions: Action[] = []

    for (const item of sortedWithdrawals) {
      // Skip rejected withdrawals (status 3)
      if (item.status === 3) {
        continue
      }

      // Stop processing if withdrawal is not completed (status != 6)
      if (item.status !== 6) {
        stopped = true
        break
      }

      // Update fetchedFrom and fetchedTo
      fetchedFrom =
        fetchedFrom === undefined
          ? item.applyTime
          : Math.min(fetchedFrom, item.applyTime)
      fetchedTo =
        fetchedTo === undefined
          ? item.applyTime
          : Math.max(fetchedTo, item.applyTime)

      // Create fee action if transactionFee > 0
      if (item.transactionFee.value > 0n) {
        const asset = getAsset(item.coin)
        if (!asset) {
          throw new AssetNotFoundException(item.coin)
        }

        actions.push({
          comment: `Withdraw ${toStringBigDecimal(item.amount)} [${item.coin}] | network: ${item.network} | tx: ${item.txId}`,
          evidence: 'system-rule',
          order: 0,
          source: `${exchange.sourcePrefix}_${config.type}_${item.id}`,
          type: 'fee',
          timestamp: item.applyTime,
          transfers: [
            {
              asset,
              amount: item.transactionFee,
              direction: 'out',
            },
          ],
        })
      }

      // Create EVM source if applicable
      if (item.network && item.txId) {
        const evmChainId = networkEvmChainIds[item.network]
        if (evmChainId && isHex(item.txId)) {
          sources.push(
            createEvmActionSource({
              chainId: evmChainId,
              transactionHash: item.txId,
            }),
          )
        }
      }

      // Create Solana source if applicable
      if (item.network === solanaNetwork && item.txId) {
        sources.push(createSolanaActionSource({ signature: item.txId }))
      }
    }

    // Sort actions by timestamp in descending order
    const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

    return {
      actions: sortedActions,
      count: response.length,
      from: fetchedFrom,
      sources,
      stopped,
      to: fetchedTo,
    }
  }

/** Binance の 出金履歴 データ取得関数 */
export const fetchApiData = createFetchApiData({ exchange: binanceExchange })
